// Game sounds, scaled by the player's volume settings (master x effects).
//
// Every sound is synthesized with WebAudio for now. To replace one with a
// recorded sound, put a file at apps/client/public/sounds/<name>.mp3 (e.g.
// sounds/damage.mp3): it is tried once, and played instead of the synth
// whenever it loads. Missing files fall back to the synth silently.
import type { Settings } from "./settings";
import { SLOT_SOUNDS, SLOT_SOUND_LEVEL, SLOT_SYNTHS, type SlotSound } from "./slotSounds";
import { SPELL_SOUNDS, SPELL_SOUND_LEVEL, SPELL_SYNTHS, type SpellSound } from "./spellSounds";
import { ROOM_SOUNDS, ROOM_SOUND_LEVEL, ROOM_SYNTHS, type RoomSound } from "./roomSounds";

/** Spell casts have their own sounds (spellSounds.ts); "spell" is the generic one. */
export type Sound = "deal" | "yourTurn" | "win" | "card" | "damage" | "spell" | "button" | SpellSound | SlotSound | RoomSound;

export const SOUNDS: readonly Sound[] = ["deal", "yourTurn", "win", "card", "damage", "spell", "button", ...SPELL_SOUNDS, ...SLOT_SOUNDS, ...ROOM_SOUNDS];

/** Base loudness of each sound before the volume settings (0..1). */
export const SOUND_LEVEL: Record<Sound, number> = {
  deal: 0.25,
  yourTurn: 0.35,
  win: 0.3,
  card: 0.35,
  damage: 0.55,
  spell: 0.3,
  button: 0.18,
  ...SPELL_SOUND_LEVEL,
  ...SLOT_SOUND_LEVEL,
  ...ROOM_SOUND_LEVEL,
};

/** Where a recorded replacement for a sound would live. */
export function soundFile(sound: Sound): string {
  return `/sounds/${sound}.mp3`;
}

/** Final gain for a sound: base level x master x effects (0 = silent). */
export function soundGain(sound: Sound, audio: Settings["audio"]): number {
  const v = SOUND_LEVEL[sound] * audio.master * audio.effects;
  return Math.min(1, Math.max(0, v));
}

/** A sound's recorded replacement file, or null when there is none. */
async function fetchSoundFile(sound: Sound): Promise<ArrayBuffer | null> {
  try {
    const res = await fetch(soundFile(sound));
    const type = res.headers.get("content-type") ?? "";
    // A missing file comes back as the app's HTML page: not a sound.
    if (!res.ok || !type.startsWith("audio/")) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Gestures that may unlock audio. For touch input the user activation comes
 * with pointerup / touchend / click, not pointerdown (iOS only lets resume()
 * work inside such a gesture), so all of them are listened to.
 */
export const UNLOCK_EVENTS = ["pointerdown", "pointerup", "touchend", "click", "keydown"] as const;

// ---------- synthesized sounds ----------

type Synth = (ctx: AudioContext, out: AudioNode, t0: number) => void;

/** One enveloped oscillator note. */
function tone(ctx: AudioContext, out: AudioNode, t0: number, freq: number, ms: number, type: OscillatorType, level = 1): void {
  const t1 = t0 + ms / 1000;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  // Short attack/decay envelope avoids clicks.
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(level, t0 + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t1);
  osc.connect(gain).connect(out);
  osc.start(t0);
  osc.stop(t1 + 0.02);
}

/** A burst of white noise through a filter, with an attack/decay envelope. */
function noise(ctx: AudioContext, out: AudioNode, t0: number, ms: number, filter: BiquadFilterType, freq: number, q: number, level: number): BiquadFilterNode {
  const length = Math.ceil((ctx.sampleRate * ms) / 1000);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const f = ctx.createBiquadFilter();
  f.type = filter;
  f.frequency.setValueAtTime(freq, t0);
  f.Q.value = q;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(level, t0 + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + ms / 1000);
  src.connect(f).connect(gain).connect(out);
  src.start(t0);
  src.stop(t0 + ms / 1000 + 0.02);
  return f;
}

const notes = (freqs: number[], noteMs: number, type: OscillatorType): Synth => (ctx, out, t0) =>
  freqs.forEach((f, i) => tone(ctx, out, t0 + (i * noteMs) / 1000, f, noteMs, type));

export const SYNTHS: Record<Sound, Synth> = {
  deal: notes([660], 70, "triangle"),
  yourTurn: notes([523, 784], 110, "sine"),
  win: notes([523, 659, 784, 1047], 110, "triangle"),

  // A card flick: a short bright swish of noise and a tiny tick as it lands.
  card: (ctx, out, t0) => {
    const swish = noise(ctx, out, t0, 90, "bandpass", 3200, 0.8, 0.9);
    swish.frequency.exponentialRampToValueAtTime(1400, t0 + 0.09);
    tone(ctx, out, t0 + 0.07, 1800, 25, "square", 0.15);
  },

  // Damage: a crackling electrical surge (buzzing saw with a jittery pitch and
  // filtered static) over a heavy low thump.
  damage: (ctx, out, t0) => {
    const buzz = ctx.createOscillator();
    const buzzGain = ctx.createGain();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    buzz.type = "sawtooth";
    buzz.frequency.setValueAtTime(140, t0);
    buzz.frequency.exponentialRampToValueAtTime(420, t0 + 0.08);
    buzz.frequency.exponentialRampToValueAtTime(90, t0 + 0.35);
    lfo.type = "square";
    lfo.frequency.value = 47;
    lfoGain.gain.value = 60;
    lfo.connect(lfoGain).connect(buzz.frequency);
    buzzGain.gain.setValueAtTime(0.0001, t0);
    buzzGain.gain.exponentialRampToValueAtTime(0.45, t0 + 0.01);
    buzzGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
    buzz.connect(buzzGain).connect(out);
    for (const o of [buzz, lfo]) {
      o.start(t0);
      o.stop(t0 + 0.42);
    }
    noise(ctx, out, t0, 260, "highpass", 2500, 0.7, 0.35);
    // The thump: a low sine falling fast in pitch.
    const thump = ctx.createOscillator();
    const thumpGain = ctx.createGain();
    thump.type = "sine";
    thump.frequency.setValueAtTime(120, t0);
    thump.frequency.exponentialRampToValueAtTime(38, t0 + 0.22);
    thumpGain.gain.setValueAtTime(0.0001, t0);
    thumpGain.gain.exponentialRampToValueAtTime(1, t0 + 0.008);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
    thump.connect(thumpGain).connect(out);
    thump.start(t0);
    thump.stop(t0 + 0.32);
  },

  // A fairy sparkle: quick rising chimes with a shimmering echo.
  spell: (ctx, out, t0) => {
    const echo = ctx.createDelay();
    const feedback = ctx.createGain();
    echo.delayTime.value = 0.11;
    feedback.gain.value = 0.38;
    echo.connect(feedback).connect(echo);
    echo.connect(out);
    const chimes = [1319, 1568, 1976, 2349, 2637, 3136];
    chimes.forEach((f, i) => {
      const t = t0 + i * 0.055;
      tone(ctx, out, t, f, 260, "sine", 0.6);
      tone(ctx, echo, t, f * 2.01, 180, "triangle", 0.18);
    });
    // A few random twinkles on top.
    for (let i = 0; i < 4; i++) tone(ctx, echo, t0 + 0.12 + Math.random() * 0.3, 3000 + Math.random() * 2000, 90, "sine", 0.25);
    // The echo loop rings on by itself: cut it once it has died away.
    setTimeout(() => {
      echo.disconnect();
      feedback.disconnect();
    }, 2500);
  },

  // A soft UI click.
  button: (ctx, out, t0) => {
    tone(ctx, out, t0, 1100, 35, "triangle", 0.8);
    noise(ctx, out, t0, 18, "highpass", 4000, 0.7, 0.3);
  },

  ...SPELL_SYNTHS,
  ...SLOT_SYNTHS,
  ...ROOM_SYNTHS,
};

export class Blips {
  private ctx: AudioContext | null = null;
  private onStateChange: (() => void) | null = null;
  /** Recorded replacements: a buffer, null = no file (use the synth), or a load in progress. */
  private readonly files = new Map<Sound, AudioBuffer | null | Promise<void>>();
  /** Recorded files' bytes (null = no file), fetched once. */
  private readonly fetched = new Map<Sound, Promise<ArrayBuffer | null>>();

  /** Call from a user gesture so browsers allow sound later. */
  unlock(): void {
    const ctx = this.context();
    if (ctx && ctx.state !== "running" && ctx.state !== "closed") {
      void ctx.resume().then(
        () => this.onStateChange?.(),
        () => undefined,
      );
    }
  }

  /**
   * Unlock audio on the next user gesture on `target`. The listeners go away
   * once audio runs, and come back whenever the browser suspends or
   * interrupts it again (e.g. a phone call on iOS). Returns a cleanup function.
   */
  installUnlock(target: EventTarget): () => void {
    const onGesture = () => this.unlock();
    let armed = false;
    const arm = () => {
      if (armed) return;
      armed = true;
      for (const type of UNLOCK_EVENTS) target.addEventListener(type, onGesture, { capture: true, passive: true });
    };
    const disarm = () => {
      if (!armed) return;
      armed = false;
      for (const type of UNLOCK_EVENTS) target.removeEventListener(type, onGesture, { capture: true });
    };
    this.onStateChange = () => {
      const state = this.ctx?.state;
      if (state === "running" || state === "closed") disarm();
      else arm();
    };
    if (this.ctx) this.ctx.onstatechange = this.onStateChange;
    arm();
    return () => {
      disarm();
      this.onStateChange = null;
      if (this.ctx) this.ctx.onstatechange = null;
    };
  }

  play(sound: Sound, audio: Settings["audio"]): void {
    const gainValue = soundGain(sound, audio);
    if (gainValue <= 0) return;
    const ctx = this.context();
    if (!ctx || ctx.state !== "running") return;
    const out = ctx.createGain();
    out.gain.value = gainValue;
    out.connect(ctx.destination);
    const t0 = ctx.currentTime + 0.01;
    const file = this.files.get(sound);
    if (file instanceof AudioBuffer) {
      const src = ctx.createBufferSource();
      src.buffer = file;
      src.connect(out);
      src.start(t0);
      return;
    }
    if (file === undefined) this.files.set(sound, this.loadFile(ctx, sound));
    SYNTHS[sound](ctx, out, t0);
  }

  /**
   * Start downloading a sound's recorded file now (e.g. the Boom trap's), so
   * the very first time it plays is the recording, not the synth. Decoding
   * waits for the audio context.
   */
  prefetch(sound: Sound): void {
    if (!this.fetched.has(sound)) this.fetched.set(sound, fetchSoundFile(sound));
    const ctx = this.ctx;
    if (ctx && !this.files.has(sound)) this.files.set(sound, this.loadFile(ctx, sound));
  }

  /** Try the recorded replacement once; remember whether there is one. */
  private async loadFile(ctx: AudioContext, sound: Sound): Promise<void> {
    try {
      let bytes = this.fetched.get(sound);
      if (!bytes) {
        bytes = fetchSoundFile(sound);
        this.fetched.set(sound, bytes);
      }
      const data = await bytes;
      if (!data) throw new Error("no file");
      // decodeAudioData detaches the buffer it is given: decode a copy.
      this.files.set(sound, await ctx.decodeAudioData(data.slice(0)));
    } catch {
      this.files.set(sound, null);
    }
  }

  /** The shared audio context, for looping sounds (see insectBuzz.ts); null when there is no WebAudio. */
  sharedContext(): AudioContext | null {
    return this.context();
  }

  private context(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor = audioContextCtor();
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor();
    } catch {
      return null;
    }
    this.ctx.onstatechange = this.onStateChange;
    // Files prefetched before there was a context: decode them now.
    for (const sound of this.fetched.keys()) if (!this.files.has(sound)) this.files.set(sound, this.loadFile(this.ctx, sound));
    return this.ctx;
  }
}

export const blips = new Blips();
